import { PermissionCode } from '@gb-ticket/shared-types';
import { Loader2Icon, PlusIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import { QueueBoard } from '@/products/helpdesk/features/queues/queue-board';
import { InactiveQueuesPanel } from '@/products/helpdesk/features/queues/inactive-queues-panel';
import {
  QueueFormDialog,
  type QueueFormValues,
} from '@/products/helpdesk/features/queues/queue-form-dialog';
import {
  useCreateQueue,
  useTicketLookups,
  useUpdateQueue,
} from '@/products/helpdesk/features/tickets/use-tickets';
import { useUserLookups } from '@/platform/features/users/use-users';
import { ApiRequestError } from '@/lib/api';

type DialogState = { mode: 'create' } | { mode: 'edit'; values: QueueFormValues } | null;

type DeactivateState = {
  id: string;
  name: string;
  openCount: number;
} | null;

export function QueuesPage() {
  const me = useMe();
  const canManage = hasPermission(me.data, PermissionCode.QUEUES_MANAGE);
  const canManageTeams = hasPermission(me.data, PermissionCode.USERS_MANAGE);
  const ticketLookups = useTicketLookups();
  const userLookups = useUserLookups({ enabled: canManage, includeInactive: true });
  const createQueue = useCreateQueue();
  const updateQueue = useUpdateQueue();
  const [dialog, setDialog] = useState<DialogState>(null);
  const [deactivate, setDeactivate] = useState<DeactivateState>(null);

  const busy = createQueue.isPending || updateQueue.isPending;

  async function handleFormSubmit(payload: {
    create?: Parameters<typeof createQueue.mutateAsync>[0];
    update?: Parameters<typeof updateQueue.mutateAsync>[0]['body'];
  }) {
    try {
      if (dialog?.mode === 'create' && payload.create) {
        await createQueue.mutateAsync(payload.create);
        toast.success('Warteschlange angelegt.');
        setDialog(null);
        return;
      }
      if (dialog?.mode === 'edit' && payload.update) {
        await updateQueue.mutateAsync({ id: dialog.values.id!, body: payload.update });
        toast.success(
          payload.update.isActive === false
            ? 'Warteschlange deaktiviert.'
            : 'Warteschlange gespeichert.',
        );
        setDialog(null);
      }
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Warteschlange konnte nicht gespeichert werden.',
      );
    }
  }

  async function confirmDeactivate() {
    if (!deactivate) {
      return;
    }
    try {
      await updateQueue.mutateAsync({ id: deactivate.id, body: { isActive: false } });
      toast.success('Warteschlange deaktiviert.');
      setDeactivate(null);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Warteschlange konnte nicht deaktiviert werden.',
      );
    }
  }

  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Warteschlangen</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Spalten per Drag & Drop umsortieren. Tickets auf eine andere Queue ziehen, um sie
            zuzuweisen.
          </p>
        </div>
        {canManage && (
          <Button type="button" onClick={() => setDialog({ mode: 'create' })}>
            <PlusIcon className="size-4" aria-hidden />
            Warteschlange hinzufügen
          </Button>
        )}
      </div>

      <QueueBoard
        canManage={canManage}
        onEditQueue={(values) => setDialog({ mode: 'edit', values })}
        onDeactivateQueue={(values) => setDeactivate(values)}
      />

      <InactiveQueuesPanel enabled={canManage} />

      <QueueFormDialog
        open={dialog !== null}
        mode={dialog?.mode ?? 'create'}
        initial={dialog?.mode === 'edit' ? dialog.values : null}
        busy={busy}
        agents={ticketLookups.data?.agents ?? []}
        teams={userLookups.data?.teams ?? []}
        canManageTeams={canManageTeams}
        onOpenChange={(open) => {
          if (!open) {
            setDialog(null);
          }
        }}
        onSubmit={handleFormSubmit}
      />

      <AlertDialog
        open={deactivate !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeactivate(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Warteschlange deaktivieren?</AlertDialogTitle>
            <AlertDialogDescription>
              {deactivate
                ? deactivate.openCount > 0
                  ? `„${deactivate.name}“ wird vom Board entfernt. ${deactivate.openCount} offene Ticket${deactivate.openCount === 1 ? '' : 's'} bleiben der Warteschlange zugeordnet und sind über die Ticketliste erreichbar.`
                  : `„${deactivate.name}“ wird vom Board entfernt. Es gibt derzeit keine offenen Tickets in dieser Warteschlange.`
                : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updateQueue.isPending}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              disabled={updateQueue.isPending}
              onClick={(event) => {
                event.preventDefault();
                void confirmDeactivate();
              }}
            >
              {updateQueue.isPending ? (
                <>
                  <Loader2Icon className="size-4 animate-spin" aria-hidden />
                  Wird deaktiviert …
                </>
              ) : (
                'Deaktivieren'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
